import {
  AGENT_OUTBOUND_EMAIL_ADDRESS_MAX_LENGTH,
  AGENT_OUTBOUND_EMAIL_FORBIDDEN_CHARS_PATTERN,
  AGENT_OUTBOUND_EMAIL_LABEL_MAX_LENGTH,
  AGENT_OUTBOUND_MAX_DESTINATIONS,
  AGENT_OUTBOUND_MAX_EMAIL_RECIPIENTS,
  AGENT_OUTBOUND_MAX_MENTIONABLE_USERS,
  AGENT_REPLY_WAIT_DEFAULT_HOURS,
  AGENT_REPLY_WAIT_DEFAULT_REMIND,
} from '@this+that/constants';
import {
  AgentOutboundDestinationInput,
  AgentOutboundDestinationKind,
  AgentOutboundEmailRecipientInput,
  AgentOutboundInput,
  AgentOutboundPlatform,
  AgentOutboundSender,
  AgentOutboundView,
  AgentReplyWait,
  AgentReplyWaitInput,
  AgentReplyWaitRule,
} from '@this+that/models';

import {
  type AgentOutboundWithScope,
  AgentRecipientScope,
  AgentSlackPeopleScope,
  DEFAULT_RECIPIENT_SCOPE,
  DEFAULT_SLACK_PEOPLE_SCOPE,
  readRecipientScope,
  readSlackPeopleScope,
} from './agentRecipientScope';

/** Teammates of a shared agent get the count only: the places themselves stay with the owner. */
export function countOutboundDestinations(
  outbound: Partial<AgentOutboundView> | null | undefined,
): number {
  return outbound?.destinationCount ?? outbound?.destinations?.length ?? 0;
}

export function countEmailRecipients(
  outbound: Partial<AgentOutboundView> | null | undefined,
): number {
  return (
    outbound?.emailRecipientCount ?? outbound?.emailRecipients?.length ?? 0
  );
}

export interface AgentReplyWaitDraft {
  enabled: boolean;
  maxWaitHours: number;
  remind: boolean;
  rule: AgentReplyWaitRule;
}

export interface AgentOutboundDraft {
  sender: AgentOutboundSender;
  destinations: AgentOutboundDestinationInput[];
  mentionableUserIds: string[];
  allowBroadcastMentions: boolean;
  emailRecipients: AgentOutboundEmailRecipientInput[];
  recipientScope: AgentRecipientScope;
  /** Absent reads as NONE, like a row saved before the setting existed. */
  slackPeopleScope?: AgentSlackPeopleScope;
  replyWait: AgentReplyWaitDraft;
}

export function readDraftPeopleScope(
  draft: Pick<AgentOutboundDraft, 'slackPeopleScope'>,
): AgentSlackPeopleScope {
  return draft.slackPeopleScope ?? DEFAULT_SLACK_PEOPLE_SCOPE;
}

export const DEFAULT_REPLY_WAIT_RULE = AgentReplyWaitRule.ALL;

export const EMPTY_REPLY_WAIT_DRAFT: AgentReplyWaitDraft = {
  enabled: false,
  maxWaitHours: AGENT_REPLY_WAIT_DEFAULT_HOURS,
  remind: AGENT_REPLY_WAIT_DEFAULT_REMIND,
  rule: DEFAULT_REPLY_WAIT_RULE,
};

export const DEFAULT_OUTBOUND_SENDER = AgentOutboundSender.BOT;

export const EMPTY_OUTBOUND_DRAFT: AgentOutboundDraft = {
  sender: DEFAULT_OUTBOUND_SENDER,
  destinations: [],
  mentionableUserIds: [],
  allowBroadcastMentions: false,
  emailRecipients: [],
  recipientScope: DEFAULT_RECIPIENT_SCOPE,
  replyWait: EMPTY_REPLY_WAIT_DRAFT,
};

export function destinationIdentity(
  destination: Pick<AgentOutboundDestinationInput, 'workspaceId' | 'channelId'>,
): string {
  return `${destination.workspaceId}:${destination.channelId}`;
}

/** GraphQL sends null for an absent object or list and the cache can drop the key; both mean "not set". */
export function replyWaitToDraft(
  replyWait: Partial<AgentReplyWait> | null | undefined,
): AgentReplyWaitDraft {
  if (!replyWait) {
    return { ...EMPTY_REPLY_WAIT_DRAFT };
  }
  return {
    enabled: replyWait.enabled ?? false,
    maxWaitHours: replyWait.maxWaitHours ?? AGENT_REPLY_WAIT_DEFAULT_HOURS,
    remind: replyWait.remind ?? AGENT_REPLY_WAIT_DEFAULT_REMIND,
    rule: replyWait.rule ?? DEFAULT_REPLY_WAIT_RULE,
  };
}

function peopleScopeToDraft(
  outbound: AgentOutboundWithScope,
): Pick<AgentOutboundDraft, 'slackPeopleScope'> {
  const slackPeopleScope = readSlackPeopleScope(outbound);
  return slackPeopleScope === AgentSlackPeopleScope.NONE
    ? {}
    : { slackPeopleScope };
}

export function outboundToDraft(
  outbound: AgentOutboundWithScope | null | undefined,
  replyWait?: Partial<AgentReplyWait> | null,
): AgentOutboundDraft {
  if (!outbound) {
    return {
      ...EMPTY_OUTBOUND_DRAFT,
      replyWait: replyWaitToDraft(replyWait),
    };
  }
  return {
    sender: outbound.sender ?? DEFAULT_OUTBOUND_SENDER,
    destinations: (outbound.destinations ?? []).map((destination) => ({
      platform: destination.platform,
      workspaceId: destination.workspaceId,
      channelId: destination.channelId,
      kind: destination.kind,
    })),
    mentionableUserIds: [...(outbound.mentionableUserIds ?? [])],
    allowBroadcastMentions: outbound.allowBroadcastMentions ?? false,
    emailRecipients: (outbound.emailRecipients ?? []).map((recipient) => ({
      address: recipient.address,
      ...(recipient.label ? { label: recipient.label } : {}),
    })),
    recipientScope: readRecipientScope(outbound),
    ...peopleScopeToDraft(outbound),
    replyWait: replyWaitToDraft(replyWait),
  };
}

/** The whole setting, never a patch. No destination, no email recipient, no people scope and the saved-only scope means the agent cannot send, so that is a clear (null). `slackPeopleScope` rides along only when it differs from `saved`: omitted keeps the stored value. */
export function draftToOutboundInput(
  draft: AgentOutboundDraft,
  saved?: AgentOutboundDraft,
): AgentOutboundInput | null {
  if (
    draft.destinations.length === 0 &&
    draft.emailRecipients.length === 0 &&
    draft.recipientScope === AgentRecipientScope.SAVED_ONLY &&
    readDraftPeopleScope(draft) === AgentSlackPeopleScope.NONE
  ) {
    return null;
  }
  const isPeopleScopeChanged =
    saved !== undefined &&
    readDraftPeopleScope(saved) !== readDraftPeopleScope(draft);
  return {
    recipientScope: draft.recipientScope,
    ...(isPeopleScopeChanged
      ? { slackPeopleScope: readDraftPeopleScope(draft) }
      : {}),
    sender: draft.sender,
    destinations: draft.destinations.map((destination) => ({
      platform: destination.platform,
      workspaceId: destination.workspaceId,
      channelId: destination.channelId,
      kind: destination.kind,
    })),
    mentionableUserIds: draft.mentionableUserIds,
    allowBroadcastMentions: draft.allowBroadcastMentions,
    emailRecipients: draft.emailRecipients.map(({ address, label }) => ({
      address,
      ...(label ? { label } : {}),
    })),
  };
}

/** Off is a clear (null): an agent that never waits has no setting rather than a disabled one. */
export function draftToReplyWaitInput(
  draft: AgentOutboundDraft,
): AgentReplyWaitInput | null {
  const { enabled, maxWaitHours, remind, rule } = draft.replyWait;
  return enabled ? { enabled, maxWaitHours, remind, rule } : null;
}

export function isReplyWaitDraftEqual(
  left: AgentReplyWaitDraft,
  right: AgentReplyWaitDraft,
): boolean {
  if (!left.enabled && !right.enabled) {
    return true;
  }
  return (
    left.enabled === right.enabled &&
    left.maxWaitHours === right.maxWaitHours &&
    left.remind === right.remind &&
    left.rule === right.rule
  );
}

const emailKey = (recipient: AgentOutboundEmailRecipientInput): string =>
  `${recipient.address}|${recipient.label ?? ''}`;

export function isOutboundDraftEqual(
  left: AgentOutboundDraft,
  right: AgentOutboundDraft,
): boolean {
  return (
    left.sender === right.sender &&
    left.allowBroadcastMentions === right.allowBroadcastMentions &&
    left.recipientScope === right.recipientScope &&
    readDraftPeopleScope(left) === readDraftPeopleScope(right) &&
    left.destinations.map(destinationIdentity).join('|') ===
      right.destinations.map(destinationIdentity).join('|') &&
    left.mentionableUserIds.join('|') === right.mentionableUserIds.join('|') &&
    left.emailRecipients.map(emailKey).join('\n') ===
      right.emailRecipients.map(emailKey).join('\n') &&
    isReplyWaitDraftEqual(left.replyWait, right.replyWait)
  );
}

/** What the save sends: `outbound` and `replyWait` each only when the owner changed it. */
export interface AgentSendingPatch {
  outbound?: AgentOutboundInput | null;
  replyWait?: AgentReplyWaitInput | null;
}

export function buildSendingPatch(
  draft: AgentOutboundDraft,
  saved: AgentOutboundDraft,
): AgentSendingPatch {
  const outboundEqual = isOutboundDraftEqual(
    { ...draft, replyWait: saved.replyWait },
    saved,
  );
  const replyWaitEqual = isReplyWaitDraftEqual(
    draft.replyWait,
    saved.replyWait,
  );
  return {
    ...(outboundEqual ? {} : { outbound: draftToOutboundInput(draft, saved) }),
    ...(replyWaitEqual ? {} : { replyWait: draftToReplyWaitInput(draft) }),
  };
}

export function hasReachedDestinationCap(draft: AgentOutboundDraft): boolean {
  return draft.destinations.length >= AGENT_OUTBOUND_MAX_DESTINATIONS;
}

export function hasReachedEmailRecipientCap(
  draft: AgentOutboundDraft,
): boolean {
  return draft.emailRecipients.length >= AGENT_OUTBOUND_MAX_EMAIL_RECIPIENTS;
}

export enum EmailRecipientError {
  INVALID = 'INVALID',
  DUPLICATE = 'DUPLICATE',
  CAP = 'CAP',
  LABEL_TOO_LONG = 'LABEL_TOO_LONG',
}

const EMAIL_SHAPE_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmailAddress(raw: string): string {
  return raw.trim().toLowerCase();
}

export function validateEmailRecipient(
  draft: AgentOutboundDraft,
  rawAddress: string,
  rawLabel: string,
): EmailRecipientError | null {
  const address = normalizeEmailAddress(rawAddress);
  if (
    address.length > AGENT_OUTBOUND_EMAIL_ADDRESS_MAX_LENGTH ||
    AGENT_OUTBOUND_EMAIL_FORBIDDEN_CHARS_PATTERN.test(address) ||
    !EMAIL_SHAPE_PATTERN.test(address)
  ) {
    return EmailRecipientError.INVALID;
  }
  if (rawLabel.trim().length > AGENT_OUTBOUND_EMAIL_LABEL_MAX_LENGTH) {
    return EmailRecipientError.LABEL_TOO_LONG;
  }
  if (draft.emailRecipients.some((r) => r.address === address)) {
    return EmailRecipientError.DUPLICATE;
  }
  if (hasReachedEmailRecipientCap(draft)) {
    return EmailRecipientError.CAP;
  }
  return null;
}

export function addEmailRecipient(
  draft: AgentOutboundDraft,
  rawAddress: string,
  rawLabel: string,
): AgentOutboundDraft {
  if (validateEmailRecipient(draft, rawAddress, rawLabel) !== null) {
    return draft;
  }
  const label = rawLabel.trim();
  return {
    ...draft,
    emailRecipients: [
      ...draft.emailRecipients,
      {
        address: normalizeEmailAddress(rawAddress),
        ...(label ? { label } : {}),
      },
    ],
  };
}

export function removeEmailRecipient(
  draft: AgentOutboundDraft,
  address: string,
): AgentOutboundDraft {
  return {
    ...draft,
    emailRecipients: draft.emailRecipients.filter((r) => r.address !== address),
  };
}

export function hasReachedMentionCap(draft: AgentOutboundDraft): boolean {
  return (
    draft.mentionableUserIds.length >= AGENT_OUTBOUND_MAX_MENTIONABLE_USERS
  );
}

export function addDestination(
  draft: AgentOutboundDraft,
  workspaceId: string,
  channelId: string,
  kind: AgentOutboundDestinationKind,
): AgentOutboundDraft {
  const candidate = { workspaceId, channelId };
  const alreadyAdded = draft.destinations.some(
    (destination) =>
      destinationIdentity(destination) === destinationIdentity(candidate),
  );
  if (alreadyAdded || hasReachedDestinationCap(draft)) {
    return draft;
  }
  return {
    ...draft,
    destinations: [
      ...draft.destinations,
      { platform: AgentOutboundPlatform.SLACK, workspaceId, channelId, kind },
    ],
  };
}

export function removeDestination(
  draft: AgentOutboundDraft,
  identity: string,
): AgentOutboundDraft {
  return {
    ...draft,
    destinations: draft.destinations.filter(
      (destination) => destinationIdentity(destination) !== identity,
    ),
  };
}

export function toggleMentionableUser(
  draft: AgentOutboundDraft,
  slackId: string,
): AgentOutboundDraft {
  if (draft.mentionableUserIds.includes(slackId)) {
    return {
      ...draft,
      mentionableUserIds: draft.mentionableUserIds.filter(
        (id) => id !== slackId,
      ),
    };
  }
  if (hasReachedMentionCap(draft)) {
    return draft;
  }
  return {
    ...draft,
    mentionableUserIds: [...draft.mentionableUserIds, slackId],
  };
}

/** Somewhere the agent can send a question: a Slack destination or a saved email address. */
export function countAskablePlaces(draft: AgentOutboundDraft): number {
  return draft.destinations.length + draft.emailRecipients.length;
}

/** People, not places: a channel counts through the people the agent may tag in it. */
export function countAskablePeople(draft: AgentOutboundDraft): number {
  const directMessages = draft.destinations.filter(
    (destination) =>
      destination.kind === AgentOutboundDestinationKind.DIRECT_MESSAGE,
  ).length;
  return (
    draft.emailRecipients.length +
    draft.mentionableUserIds.length +
    directMessages
  );
}

/** A bot only sees conversations it belongs to, so it cannot be pointed at the owner's private one. */
export function hasBotDirectMessageConflict(
  draft: AgentOutboundDraft,
): boolean {
  return (
    draft.sender === AgentOutboundSender.BOT &&
    draft.destinations.some(
      (destination) =>
        destination.kind === AgentOutboundDestinationKind.DIRECT_MESSAGE,
    )
  );
}

/** The people scope only counts while the viewer may use it; flag off reads as NONE so no copy changes. */
export function hasActivePeopleScope(
  draft: Pick<AgentOutboundDraft, 'slackPeopleScope'>,
  canMessagePeople: boolean,
): boolean {
  return (
    canMessagePeople &&
    readDraftPeopleScope(draft) !== AgentSlackPeopleScope.NONE
  );
}
